import { useEffect, useState, useRef } from 'react';
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/20/solid';
import { ReactComponent as LoadingSvg } from '../../../assets/svg/loading-half-status.svg';
import { FORMAT, formatDate } from '../../../lib/utils';
import request from '../../../lib/request';
import Title from '../../../components/dialog/title';

const getCurrentDateFormat = () => {
  return `${formatDate(new Date())}T${formatDate(new Date(), FORMAT.HOURMIN)}`;
};

const UpdateDialog = ({
  dialogRef,
  closeDialog,
  mealInfo,
  isUpdateDialogOpen,
  handleRefresh,
  user,
  openPayDialog,
}) => {
  const ingredientDialogRef = useRef(null);
  const editDialogRef = useRef(null);
  const analyzingTimeRef = useRef(0);
  const analyzingRef = useRef(null);
  const apiControllerRef = useRef(new AbortController());

  const [date, setDate] = useState(getCurrentDateFormat());
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [totalCalories, setTotalCalories] = useState({
    calories: 0,
    proteins: 0,
    carbohydrates: 0,
    fats: 0,
  });
  const [newIngredient, setNewIngredient] = useState(null);
  const [aiResult, setAiResult] = useState([]);
  const [isEditingIndex, setIsEditingIndex] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [credit, setCredit] = useState(0);

  useEffect(() => {
    if (isUpdateDialogOpen) {
      setDate(`${mealInfo.logDate}T${mealInfo.logTime}`);
      setTag(mealInfo.tags.map(({ tag }) => tag.name).join(', '));
      setDescription(mealInfo.description || '');
      setSource(mealInfo.photoUrl);
      setIngredients(mealInfo.ingredients);

      setTotalCalories(
        mealInfo.ingredients.reduce(
          (acc, cur) => ({
            calories: acc.calories + cur.calories,
            proteins: acc.proteins + cur.proteins,
            carbohydrates: acc.carbohydrates + cur.carbohydrates,
            fats: acc.fats + cur.fats,
          }),
          { calories: 0, proteins: 0, carbohydrates: 0, fats: 0 }
        )
      );
    }
  }, [isUpdateDialogOpen]);

  const resetState = () => {
    setDate(getCurrentDateFormat());
    setTag('');
    setDescription('');
  };

  const handleDateChange = (e) => {
    setDate(e.target.value);
  };

  const handleDescriptionChange = (e) => {
    setDescription(String(e.target.value));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const [logDate, logTime] = date.split('T');

      await request.put(`/meal/${mealInfo.id}`, {
        logDate,
        logTime,
        tag: tag ? tag.replace(/\s/g, '').split(',') : [],
        description,
        ingredients: ingredients.map(
          ({ id, name, portion, calories, proteins, carbohydrates, fats }) => ({
            id,
            name,
            portion: Number(portion),
            calories: Number(calories),
            proteins: Number(proteins),
            carbohydrates: Number(carbohydrates),
            fats: Number(fats),
          })
        ),
        calorie: Number(totalCalories.calories),
        consumedAt: new Date(date).toISOString(),
      });

      await handleRefresh();

      setSource(null);
      resetState();
      closeDialog();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSource(null);
    resetState();
    closeDialog();
  };

  const removeIngredient = (index) => async () => {
    if (window.confirm('Are you sure you want to remove this ingredient?')) {
      try {
        await request.delete(`/meal/ingredient/${ingredients[index].id}`);
        Object.entries(ingredients[index]).forEach(([key, value]) => {
          setTotalCalories((prev) => ({
            ...prev,
            [key]: prev[key] - Number(value),
          }));
        });
        setIngredients((prev) => prev.filter((_, i) => i !== index));
      } catch (err) {
        console.log(err);
      }
    }
  };

  const openAddCustomIngredient = () => {
    const ingredient = {
      calories: 0,
      carbohydrates: 0,
      fats: 0,
      name: '',
      portion: 0,
      proteins: 0,
    };
    setNewIngredient(ingredient);
    openEditDialog();
  };

  const editExistingIngredient = (index) => () => {
    setNewIngredient(ingredients[index]);
    setIsEditingIndex(index);
    openEditDialog();
  };

  const addNewIngredientToList = () => {
    if (!newIngredient.name) {
      alert('Please enter an ingredient name');
      return;
    }

    setIngredients((prev) => [...prev, newIngredient]);
    setTotalCalories((prev) => ({
      ...prev,
      calories: prev.calories + Number(newIngredient.calories),
      proteins: prev.proteins + Number(newIngredient.proteins),
      carbohydrates: prev.carbohydrates + Number(newIngredient.carbohydrates),
      fats: prev.fats + Number(newIngredient.fats),
    }));
    setNewIngredient(null);
    setAiResult([]);
    closeEditDialog();
  };

  const updateExistingIngredient = () => {
    if (typeof isEditingIndex !== 'number') {
      return;
    }

    const newCalculation = { ...totalCalories };
    newCalculation.calories -= ingredients[isEditingIndex].calories;
    newCalculation.proteins -= ingredients[isEditingIndex].proteins;
    newCalculation.carbohydrates -= ingredients[isEditingIndex].carbohydrates;
    newCalculation.fats -= ingredients[isEditingIndex].fats;

    setIngredients((prev) => {
      const newList = [...prev];
      newList[isEditingIndex] = newIngredient;
      return newList;
    });
    setTotalCalories({
      calories: newCalculation.calories + Number(newIngredient.calories),
      proteins: newCalculation.proteins + Number(newIngredient.proteins),
      carbohydrates:
        newCalculation.carbohydrates + Number(newIngredient.carbohydrates),
      fats: newCalculation.fats + Number(newIngredient.fats),
    });
    setNewIngredient(null);
    setAiResult([]);
    setIsEditingIndex(null);
    closeEditDialog();
  };

  const analyzeDescription = async () => {
    if (!newIngredient.name) {
      return alert('Please enter an ingredient name');
    }

    setIsAnalyzing(true);

    analyzingRef.current = setInterval(() => {
      analyzingTimeRef.current++;
      if (analyzingTimeRef.current === 30) {
        alert('Analyzing failed. Please try again.');
        setTimeout(() => {
          apiControllerRef.current.abort();
        }, 1000);
      }
    }, 1000);

    try {
      const { result } = await request.post(
        '/meal/ai-scan',
        { description: `${newIngredient.name} ${newIngredient.portion}g` },
        apiControllerRef.current.signal
      );
      if (result?.ingredients) {
        const newCalculation = {
          portion: 0,
          calories: 0,
          carbohydrates: 0,
          fats: 0,
          proteins: 0,
        };

        result.ingredients.forEach((item) => {
          newCalculation.portion += item.portion;
          newCalculation.calories += item.calories;
          newCalculation.carbohydrates += item.carbohydrates;
          newCalculation.fats += item.fats;
          newCalculation.proteins += item.proteins;
        });

        setNewIngredient((prev) => ({
          ...prev,
          portion: Number(newCalculation.portion.toFixed(1)),
          calories: Number(newCalculation.calories.toFixed(1)),
          carbohydrates: Number(newCalculation.carbohydrates.toFixed(1)),
          fats: Number(newCalculation.fats.toFixed(1)),
          proteins: Number(newCalculation.proteins.toFixed(1)),
        }));
        setAiResult(result.ingredients);
      }
    } catch (err) {
      if (err.name === 'CanceledError') {
        clearInterval(analyzingRef.current);
        setIsAnalyzing(false);
        analyzingTimeRef.current = 0;
        apiControllerRef.current = new AbortController();
        return alert('AI Analyzing failed. Please try again.');
      }
      if (err.response.data.error === 'OverCreditLimit') {
        clearInterval(analyzingRef.current);
        setIsAnalyzing(false);
        analyzingTimeRef.current = 0;
        apiControllerRef.current = new AbortController();
        alert(
          'You have reached the maximum number of AI Scans. Please upgrade to PREMIUM to continue.'
        );
        return openPayDialog();
      }
    }

    clearInterval(analyzingRef.current);
    setIsAnalyzing(false);
    analyzingTimeRef.current = 0;
    getCredit();
  };

  const getCredit = async () => {
    try {
      if (!user.activated && !user.programRegistered) {
        const { result } = await request.get('/user/free/credit');
        if (result) {
          setCredit(result.aiCredit);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openIngredientDialog = () => {
    if (ingredientDialogRef.current) {
      ingredientDialogRef.current.showModal();
    }
  };

  const closeIngredientDialog = () => {
    if (ingredientDialogRef.current) {
      ingredientDialogRef.current.close();
    }
  };

  const openEditDialog = () => {
    if (editDialogRef.current) {
      editDialogRef.current.showModal();
    }
  };

  const closeEditDialog = () => {
    if (isAnalyzing) {
      apiControllerRef.current.abort('Cancelled');
    }
    if (editDialogRef.current) {
      editDialogRef.current.close();
    }
    setIsEditingIndex(null);
    setNewIngredient(null);
    setAiResult([]);
    setIsAnalyzing(false);
  };

  return (
    <dialog
      ref={dialogRef}
      className='p-4 w-full h-full max-w-lg max-h-full bg-secondary scrollbar-hidden'
    >
      <Title
        title='Edit Meal'
        onClick={handleCancel}
        color='white'
      />
      <div className='flex justify-center aspect-square rounded-xl overflow-hidden mb-4 border border-tertiary'>
        <img
          src={source}
          alt='source'
          className='w-full h-full object-cover rounded-xl'
        />
      </div>
      <div className='flex items-center justify-between gap-2 mb-4'>
        <label className='text-white font-semibold'>Date</label>
        <input
          type='datetime-local'
          max={formatDate(new Date(), FORMAT.DATEHOURMIN)}
          value={date}
          onChange={handleDateChange}
          disabled={loading}
          className='rounded-full px-2 py-1 bg-tertiary  text-white'
        />
      </div>
      <div className='mb-4'>
        <div className='text-sm'>
          <div className='grid grid-cols-2 gap-1 rounded-xl text-white'>
            <div className='flex justify-between items-center border bg-tertiary/30 rounded-full p-2'>
              <div>Calories</div>
              <div>{totalCalories.calories.toFixed(1)} Cal</div>
            </div>
            <div className='flex justify-between items-center border rounded-full p-2 border-protein bg-protein/30'>
              <div>Protein</div>
              <div>{totalCalories.proteins.toFixed(1)} g</div>
            </div>
            <div className='flex justify-between items-center border rounded-full p-2 border-carbs bg-carbs/30'>
              <div>Carbs</div>
              <div>{totalCalories.carbohydrates.toFixed(1)} g</div>
            </div>
            <div className='flex justify-between items-center border rounded-full p-2 border-fats bg-fats/30'>
              <div>Fat</div>
              <div>{totalCalories.fats.toFixed(1)} g</div>
            </div>
          </div>
          <div className='flex justify-center items-center my-4 text-white'>
            <a
              className='underline cursor-pointer'
              onClick={openIngredientDialog}
            >
              View ingredients
            </a>
          </div>
        </div>

        <div className='flex items-center gap-2 justify-between mb-4'>
          <label className='text-white font-semibold'>Tag</label>
          <div className='flex items-center gap-2 text-sm'>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Breakfast'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Breakfast')}
            >
              Breakfast
            </button>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Lunch'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Lunch')}
            >
              Lunch
            </button>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Dinner'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Dinner')}
            >
              Dinner
            </button>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Snack'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Snack')}
            >
              Snack
            </button>
          </div>
        </div>
        <div className='flex flex-col gap-2 mb-4'>
          <label className='text-white font-semibold'>Note</label>
          <textarea
            value={description}
            rows={2}
            onChange={handleDescriptionChange}
            disabled={loading}
            placeholder='Add any additional notes or details'
            className='rounded-xl p-2 bg-tertiary  text-white resize-none'
          />
        </div>
      </div>
      <div className='flex justify-between items-center text-sm mb-4'>
        <button
          className='rounded-full w-full py-2 bg-accent font-bold'
          onClick={handleSubmit}
          disabled={loading}
        >
          Save
        </button>
      </div>
      {loading && (
        <div className='absolute animate-bounce top-4 flex justify-center items-center'>
          <LoadingSvg className='animate-spin' />
        </div>
      )}

      {/* Ingredient Dialog */}
      <dialog
        ref={ingredientDialogRef}
        className='w-full h-full max-w-lg max-h-full bg-transparent'
        onCancel={closeIngredientDialog}
      >
        <div
          className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 text-white flex flex-col border border-b-0 border-tertiary`}
        >
          <div className='text-right text-2xl mr-1'>
            <span
              onClick={closeIngredientDialog}
              className='cursor-pointer'
            >
              🅧
            </span>
          </div>
          <div className=''>
            <div className='mb-4'>
              <div className='opacity-40'>Your plate</div>
              <div className='text-xl font-bold text-accent'>
                {totalCalories.calories} Cal
              </div>
            </div>
            <div className='divide-y divide-tertiary space-y-4 max-h-96 overflow-y-auto scrollbar-hidden rounded-xl h-full'>
              {ingredients.map((item, i) => (
                <div
                  key={i}
                  className='pt-4'
                >
                  <div className='flex justify-between gap-2 mb-2 px-1'>
                    <div className='flex gap-2 items-end'>
                      <div className='font-bold line-clamp-1'>{item.name}</div>
                      <div className='opacity-40 flex-shrink-0 text-sm'>
                        {item.portion} g
                      </div>
                    </div>
                    <div className='flex gap-2 items-center'>
                      <PencilSquareIcon
                        className='size-5 opacity-40'
                        onClick={editExistingIngredient(i)}
                      />
                      <TrashIcon
                        className='size-5 opacity-40'
                        onClick={removeIngredient(i)}
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-2 gap-1'>
                    <div className='flex justify-between items-center border rounded-full px-2 py-1 bg-tertiary/30'>
                      <div>Calories</div>
                      <div>{item.calories} Cal</div>
                    </div>
                    <div className='flex justify-between items-center border rounded-full px-2 py-1 border-protein bg-protein/30'>
                      <div>Protein</div>
                      <div>{item.proteins} g</div>
                    </div>
                    <div className='flex justify-between items-center border rounded-full px-2 py-1 border-carbs bg-carbs/30'>
                      <div>Carbs</div>
                      <div>{item.carbohydrates} g</div>
                    </div>
                    <div className='flex justify-between items-center border rounded-full px-2 py-1 border-fats bg-fats/30'>
                      <div>Fat</div>
                      <div>{item.fats} g</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className='flex gap-2 items-center flex-shrink-0 pt-4'>
              <button
                className='flex-1 px-3 py-2 gap-2 rounded-full text-center border border-accent text-accent font-bold'
                onClick={openAddCustomIngredient}
              >
                Add more food
              </button>
              <button
                className='flex-1 px-3 py-2 gap-2 rounded-full text-center bg-accent text-black font-bold'
                onClick={closeIngredientDialog}
              >
                Save ingredients
              </button>
            </div>

            {/* Edit Dialog */}
            <dialog
              ref={editDialogRef}
              className='w-full h-full max-w-lg max-h-full bg-transparent'
              onCancel={closeEditDialog}
            >
              <div
                className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 text-white flex flex-col border border-b-0 border-tertiary`}
              >
                <div className='flex items-center justify-between mb-2'>
                  <div className='font-bold'>Add ingredient</div>
                  <span
                    className='text-2xl cursor-pointer'
                    onClick={closeEditDialog}
                  >
                    🅧
                  </span>
                </div>
                {newIngredient && (
                  <div className={`mb-4 ${isAnalyzing ? 'animate-pulse' : ''}`}>
                    <div className='flex items-center justify-between gap-1 mb-1'>
                      <div className='w-1/2'>
                        <input
                          type='text'
                          value={newIngredient?.name || ''}
                          placeholder='Ingredient name'
                          onChange={(e) =>
                            setNewIngredient({
                              ...newIngredient,
                              name: e.target.value,
                            })
                          }
                          className='rounded-full p-2 bg-tertiary text-white w-full'
                        />
                      </div>
                      <div className='flex items-center gap-2 w-1/2 justify-between rounded-full px-2 py-1 bg-tertiary'>
                        <div>Portion</div>
                        <div className='flex items-center gap-2'>
                          <input
                            type='number'
                            value={newIngredient?.portion || 0}
                            onChange={(e) =>
                              setNewIngredient({
                                ...newIngredient,
                                portion: e.target.value,
                              })
                            }
                            className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                          />
                          <span>g</span>
                        </div>
                      </div>
                    </div>
                    <div className='mb-4'>
                      {aiResult.length > 0 && (
                        <div className='flex items-center gap-1 text-xs flex-wrap italic text-white/50 justify-center'>
                          {aiResult.map((item, i) => (
                            <span key={i}>
                              {item.name}
                              {i < aiResult.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className='grid grid-cols-2 gap-1'>
                      <div className='flex justify-between items-center border rounded-full px-2 py-1 bg-tertiary/30'>
                        <div>Calories</div>
                        <div className='flex items-center gap-2'>
                          <input
                            type='number'
                            value={newIngredient?.calories || 0}
                            onChange={(e) =>
                              setNewIngredient({
                                ...newIngredient,
                                calories: e.target.value,
                              })
                            }
                            className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                          />
                          <span>Cal</span>
                        </div>
                      </div>
                      <div className='flex justify-between items-center border rounded-full px-2 py-1 border-protein bg-protein/30'>
                        <div>Protein</div>
                        <div>
                          <div className='flex items-center gap-2'>
                            <input
                              type='number'
                              value={newIngredient?.proteins || 0}
                              onChange={(e) =>
                                setNewIngredient({
                                  ...newIngredient,
                                  proteins: e.target.value,
                                })
                              }
                              className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                            />
                            <span>g</span>
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-between items-center border rounded-full px-2 py-1 border-carbs bg-carbs/30'>
                        <div>Carbs</div>
                        <div className='flex items-center gap-2'>
                          <input
                            type='number'
                            value={newIngredient?.carbohydrates || 0}
                            onChange={(e) =>
                              setNewIngredient({
                                ...newIngredient,
                                carbohydrates: e.target.value,
                              })
                            }
                            className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                          />
                          <span>g</span>
                        </div>
                      </div>
                      <div className='flex justify-between items-center border rounded-full px-2 py-1 border-fats bg-fats/30'>
                        <div>Fat</div>
                        <div className='flex items-center gap-2'>
                          <input
                            type='number'
                            value={newIngredient?.fats || 0}
                            onChange={(e) =>
                              setNewIngredient({
                                ...newIngredient,
                                fats: e.target.value,
                              })
                            }
                            className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                          />
                          <span>g</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className='flex items-center gap-2'>
                  <button
                    className={`flex-1 rounded-full py-2 bg-gradient-to-r from-[#fc00ff] to-[#00dbde] text-white text-center font-bold ${
                      !newIngredient?.name && 'opacity-50'
                    } ${isAnalyzing && 'animate-pulse'}`}
                    disabled={!newIngredient?.name || isAnalyzing}
                    onClick={analyzeDescription}
                  >
                    {isAnalyzing ? 'Analyzing...' : 'Analyze with AI'}
                    {!user?.activated && !user?.programRegistered && (
                      <span className='text-xs'> ({credit})</span>
                    )}
                  </button>
                  <button
                    className='flex-1 rounded-full py-2 bg-accent text-black font-bold'
                    onClick={
                      typeof isEditingIndex === 'number'
                        ? updateExistingIngredient
                        : addNewIngredientToList
                    }
                    disabled={!newIngredient?.name || isAnalyzing}
                  >
                    {typeof isEditingIndex === 'number' ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            </dialog>
          </div>
        </div>
      </dialog>
    </dialog>
  );
};

export default UpdateDialog;
